/**
 * TermSelector.tsx
 * Modal for selecting SynonymGroups by CATEGORY from Global Glossary.
 * User selects categories (groups), and all SynonymGroups in those categories are copied.
 */
import React, { useState, useMemo } from 'react';
import { X, Check, Search, BookOpen, ChevronRight, CheckSquare, Square, Folder } from 'lucide-react';
import { SynonymGroup } from '../types';
import { useGlossaryStore } from '../store/useGlossaryStore';

interface TermSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (selectedGroups: SynonymGroup[]) => void;
    currentSelection: SynonymGroup[];
}

const TermSelector: React.FC<TermSelectorProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currentSelection
}) => {
    const { synonymGroups: globalGroups } = useGlossaryStore();
    const [searchTerm, setSearchTerm] = useState('');

    // Group SynonymGroups by category
    const categories = useMemo(() => {
        const grouped: Record<string, SynonymGroup[]> = {};

        globalGroups.forEach(group => {
            const cat = group.category || 'Uncategorized';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(group);
        });

        return Object.entries(grouped)
            .map(([name, groups]) => ({ name, groups, count: groups.length }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [globalGroups]);

    // Filter categories by search
    const filteredCategories = useMemo(() => {
        if (!searchTerm) return categories;
        const lower = searchTerm.toLowerCase();
        return categories.filter(cat =>
            cat.name.toLowerCase().includes(lower) ||
            cat.groups.some(g =>
                g.name.toLowerCase().includes(lower) ||
                g.sources.some(s => s.toLowerCase().includes(lower))
            )
        );
    }, [categories, searchTerm]);

    // Get currently selected category names from current selection
    const getSelectedCategories = () => {
        const catNames = new Set<string>();
        currentSelection.forEach(g => catNames.add(g.category || 'Uncategorized'));
        return catNames;
    };

    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(getSelectedCategories);

    const toggleCategory = (catName: string) => {
        setSelectedCategories(prev => {
            const next = new Set(prev);
            if (next.has(catName)) {
                next.delete(catName);
            } else {
                next.add(catName);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedCategories.size === filteredCategories.length) {
            setSelectedCategories(new Set());
        } else {
            setSelectedCategories(new Set(filteredCategories.map(c => c.name)));
        }
    };

    const handleConfirm = () => {
        // Collect all SynonymGroups from selected categories
        const selectedGroups: SynonymGroup[] = [];
        categories.forEach(cat => {
            if (selectedCategories.has(cat.name)) {
                selectedGroups.push(...cat.groups);
            }
        });
        onConfirm(selectedGroups);
        onClose();
    };

    // Count total terms in selected categories
    const totalSelectedTerms = useMemo(() => {
        let count = 0;
        categories.forEach(cat => {
            if (selectedCategories.has(cat.name)) {
                count += cat.count;
            }
        });
        return count;
    }, [categories, selectedCategories]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                    <div>
                        <h2 className="text-lg font-black text-slate-800">Chọn nhóm thuật ngữ</h2>
                        <p className="text-xs text-slate-500">
                            Chọn các danh mục từ Từ điển dùng chung.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search & Actions */}
                <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Tìm danh mục..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:border-indigo-500 outline-none"
                        />
                    </div>
                    <button
                        onClick={toggleAll}
                        className="px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg flex items-center gap-2 shrink-0"
                    >
                        {selectedCategories.size === filteredCategories.length ? (
                            <><Square className="w-4 h-4" /> Bỏ chọn tất cả</>
                        ) : (
                            <><CheckSquare className="w-4 h-4" /> Chọn tất cả</>
                        )}
                    </button>
                </div>

                {/* Category List */}
                <div className="flex-1 overflow-y-auto p-4">
                    {categories.length === 0 ? (
                        <div className="text-center py-12">
                            <BookOpen className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                            <p className="text-slate-500 font-medium">Chưa có danh mục nào</p>
                            <p className="text-xs text-slate-400 mt-1">
                                Vào trang Từ điển thuật ngữ để tạo và sắp xếp thuật ngữ.
                            </p>
                        </div>
                    ) : filteredCategories.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-slate-500">Không có kết quả cho "{searchTerm}"</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredCategories.map(category => (
                                <button
                                    key={category.name}
                                    onClick={() => toggleCategory(category.name)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${selectedCategories.has(category.name)
                                            ? 'bg-indigo-50 border-2 border-indigo-300'
                                            : 'bg-white border border-slate-200 hover:border-slate-300'
                                        }`}
                                >
                                    <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${selectedCategories.has(category.name)
                                            ? 'bg-indigo-600 text-white'
                                            : 'border-2 border-slate-300'
                                        }`}>
                                        {selectedCategories.has(category.name) && <Check className="w-3 h-3" />}
                                    </div>
                                    <Folder className={`w-5 h-5 shrink-0 ${selectedCategories.has(category.name)
                                            ? 'text-indigo-600'
                                            : 'text-slate-400'
                                        }`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-slate-800">{category.name}</p>
                                        <p className="text-xs text-slate-500">{category.count} thuật ngữ</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-slate-300" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                    <p className="text-sm text-slate-500">
                        <span className="font-bold text-indigo-600">{selectedCategories.size}</span> danh mục
                        ({totalSelectedTerms} thuật ngữ)
                    </p>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium text-sm"
                        >
                            Hủy
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 flex items-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            Xác nhận chọn
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermSelector;
